import { captureException } from '@/utils/sentry';
import { EditScopeKind } from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import { Fennel } from '@/analytics/fennel/fennel';
import { PricingGroupBy } from '@/domain/pricing/types';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesPortalAnalyticMetadata } from '@/experiences/sales-portal/analytics/fennel/registry';

export enum PricingPageSource {
  AGENT_REVIEW = 'agent_review',
  BOOKMARKED = 'bookmarked',
  CUSTOMER_DETAIL = 'customer_detail',
  ITEM_DETAILS = 'item_details',
  REVIEW = 'review',
}

export const PAGE_SOURCE_BY_EDIT_SCOPE: Record<
  EditScopeKind,
  PricingPageSource
> = {
  [EditScopeKind.Bookmarked]: PricingPageSource.BOOKMARKED,
  [EditScopeKind.Restaurant]: PricingPageSource.CUSTOMER_DETAIL,
  [EditScopeKind.Review]: PricingPageSource.REVIEW,
  [EditScopeKind.VariantPack]: PricingPageSource.ITEM_DETAILS,
};

export interface PricingInlineEditPayload {
  pageSource: PricingPageSource;
  quickAction: string | null;
  restaurantUUID: string;
  supplierUUID: string;
  variantPackUUID: string;
}

export interface PricingBulkEditPayload {
  pageSource: PricingPageSource;
  quickAction: string;
  restaurantUUIDs: string[];
  supplierUUIDs: string[];
  variantPackUUIDs: string[];
}

export interface PricingInlineReversionPayload {
  pageSource: PricingPageSource;
  restaurantUUID: string;
  supplierUUID: string;
  variantPackUUID: string;
}

export interface PricingBulkReversionPayload {
  pageSource: PricingPageSource;
  restaurantUUIDs: string[];
  supplierUUIDs: string[];
  variantPackUUIDs: string[];
}

export interface PricingInlineBookmarkPayload {
  hasNote: boolean;
  pageSource: PricingPageSource;
  restaurantUUID: string;
  supplierUUID: string;
  variantPackUUID: string;
}

export interface PricingBulkBookmarkPayload {
  hasNote: boolean;
  pageSource: PricingPageSource;
  restaurantUUIDs: string[];
  supplierUUIDs: string[];
  variantPackUUIDs: string[];
}

export interface PricingInlineUnbookmarkPayload {
  pageSource: PricingPageSource;
  restaurantUUID: string;
  supplierUUID: string;
  variantPackUUID: string;
}

export interface PricingBulkUnbookmarkPayload {
  pageSource: PricingPageSource;
  restaurantUUIDs: string[];
  supplierUUIDs: string[];
  variantPackUUIDs: string[];
}

export interface PricingGroupByTogglePayload {
  groupBy: PricingGroupBy;
  pageSource: PricingPageSource;
}

export interface PricingUndoPayload {
  pageSource: PricingPageSource;
}

export interface PricingPublishPayload {
  editRestaurantVPPairs: string[];
  numberOfEdits: number;
  supplierUUIDs: string[];
}

export interface PricingPublishErrorPayload {
  error: string;
}

export interface PricingAgentDecisionPayload {
  recommendationUUID: string;
  restaurantUUID: string;
  supplierUUID: string;
  variantPackUUID: string;
}

export interface PricingAgentOverridePayload {
  inputType: string;
  quickAction: string | null;
  recommendationUUID: string;
  restaurantUUID: string;
  supplierUUID: string;
  variantPackUUID: string;
}

export interface PricingAgentPublishPayload {
  acceptedCount: number;
  overriddenCount: number;
  rejectedCount: number;
  supplierUUIDs: string[];
  taskTypeUUID: string;
}

export interface PricingAgentPublishErrorPayload {
  error: string;
  taskTypeUUID: string;
}

export enum PricingAnalyticEvent {
  AGENT_ACCEPT = 'sales_portal.pricing.agent_accept',
  AGENT_OVERRIDE = 'sales_portal.pricing.agent_override',
  AGENT_OVERRIDE_REVERSION = 'sales_portal.pricing.agent_override_reversion',
  AGENT_PUBLISH = 'sales_portal.pricing.agent_publish',
  AGENT_PUBLISH_ERROR = 'sales_portal.pricing.agent_publish_error',
  AGENT_REJECT = 'sales_portal.pricing.agent_reject',
  AGENT_RESET = 'sales_portal.pricing.agent_reset',
  AGENT_UNDO = 'sales_portal.pricing.agent_undo',
  BULK_BOOKMARK = 'sales_portal.pricing.bulk_bookmark',
  BULK_EDIT = 'sales_portal.pricing.bulk_edit',
  BULK_REVERSION = 'sales_portal.pricing.bulk_reversion',
  BULK_UNBOOKMARK = 'sales_portal.pricing.bulk_unbookmark',
  GROUP_BY_TOGGLE = 'sales_portal.pricing.group_by_toggle',
  INLINE_BOOKMARK = 'sales_portal.pricing.inline_bookmark',
  INLINE_EDIT = 'sales_portal.pricing.inline_edit',
  INLINE_REVERSION = 'sales_portal.pricing.inline_reversion',
  INLINE_UNBOOKMARK = 'sales_portal.pricing.inline_unbookmark',
  LOAD_AGENT_REVIEW_PAGE = 'sales_portal.pricing.load_agent_review_page',
  LOAD_BOOKMARKED_PAGE = 'sales_portal.pricing.load_bookmarked_page',
  LOAD_COST_CHANGES_CUSTOMER_DETAIL = 'sales_portal.pricing.load_cost_changes_customer_detail',
  LOAD_COST_CHANGES_CUSTOMER_LIST = 'sales_portal.pricing.load_cost_changes_customer_list',
  LOAD_COST_CHANGES_ITEM_DETAILS = 'sales_portal.pricing.load_cost_changes_item_details',
  LOAD_COST_CHANGES_ITEM_LIST = 'sales_portal.pricing.load_cost_changes_item_list',
  LOAD_REVIEW_PAGE = 'sales_portal.pricing.load_review_page',
  PUBLISH = 'sales_portal.pricing.publish',
  PUBLISH_ERROR = 'sales_portal.pricing.publish_error',
  UNDO = 'sales_portal.pricing.undo',
}

interface PricingAnalyticPayloads {
  [PricingAnalyticEvent.AGENT_ACCEPT]: PricingAgentDecisionPayload;
  [PricingAnalyticEvent.AGENT_OVERRIDE]: PricingAgentOverridePayload;
  [PricingAnalyticEvent.AGENT_OVERRIDE_REVERSION]: PricingAgentDecisionPayload;
  [PricingAnalyticEvent.AGENT_PUBLISH]: PricingAgentPublishPayload;
  [PricingAnalyticEvent.AGENT_PUBLISH_ERROR]: PricingAgentPublishErrorPayload;
  [PricingAnalyticEvent.AGENT_REJECT]: PricingAgentDecisionPayload;
  [PricingAnalyticEvent.AGENT_RESET]: PricingAgentDecisionPayload;
  [PricingAnalyticEvent.AGENT_UNDO]: Record<string, never>;
  [PricingAnalyticEvent.BULK_BOOKMARK]: PricingBulkBookmarkPayload;
  [PricingAnalyticEvent.BULK_EDIT]: PricingBulkEditPayload;
  [PricingAnalyticEvent.BULK_REVERSION]: PricingBulkReversionPayload;
  [PricingAnalyticEvent.BULK_UNBOOKMARK]: PricingBulkUnbookmarkPayload;
  [PricingAnalyticEvent.GROUP_BY_TOGGLE]: PricingGroupByTogglePayload;
  [PricingAnalyticEvent.INLINE_BOOKMARK]: PricingInlineBookmarkPayload;
  [PricingAnalyticEvent.INLINE_EDIT]: PricingInlineEditPayload;
  [PricingAnalyticEvent.INLINE_REVERSION]: PricingInlineReversionPayload;
  [PricingAnalyticEvent.INLINE_UNBOOKMARK]: PricingInlineUnbookmarkPayload;
  [PricingAnalyticEvent.LOAD_AGENT_REVIEW_PAGE]: Record<string, never>;
  [PricingAnalyticEvent.LOAD_BOOKMARKED_PAGE]: Record<string, never>;
  [PricingAnalyticEvent.LOAD_COST_CHANGES_CUSTOMER_DETAIL]: Record<
    string,
    never
  >;
  [PricingAnalyticEvent.LOAD_COST_CHANGES_CUSTOMER_LIST]: Record<string, never>;
  [PricingAnalyticEvent.LOAD_COST_CHANGES_ITEM_DETAILS]: Record<string, never>;
  [PricingAnalyticEvent.LOAD_COST_CHANGES_ITEM_LIST]: Record<string, never>;
  [PricingAnalyticEvent.LOAD_REVIEW_PAGE]: Record<string, never>;
  [PricingAnalyticEvent.PUBLISH]: PricingPublishPayload;
  [PricingAnalyticEvent.PUBLISH_ERROR]: PricingPublishErrorPayload;
  [PricingAnalyticEvent.UNDO]: PricingUndoPayload;
}

const pricingEventsMetadata: {
  [K in PricingAnalyticEvent]: SalesPortalAnalyticMetadata;
} = {
  [PricingAnalyticEvent.AGENT_ACCEPT]: {
    description:
      'When a pricing agent recommendation is accepted on the agent review page',
    eventName: 'Pricing Agent Accept',
    origin: SALES_PORTAL_ROUTES.PricingAgentReview,
  },
  [PricingAnalyticEvent.AGENT_OVERRIDE]: {
    description:
      'When a pricing agent recommendation is overridden via manual edit or quick action on the agent review page',
    eventName: 'Pricing Agent Override',
    origin: SALES_PORTAL_ROUTES.PricingAgentReview,
  },
  [PricingAnalyticEvent.AGENT_OVERRIDE_REVERSION]: {
    description:
      'When a pricing agent override is reverted on the agent review page',
    eventName: 'Pricing Agent Override Reversion',
    origin: SALES_PORTAL_ROUTES.PricingAgentReview,
  },
  [PricingAnalyticEvent.AGENT_PUBLISH]: {
    description: 'When pricing agent recommendations are published',
    eventName: 'Pricing Agent Publish',
    origin: SALES_PORTAL_ROUTES.PricingAgentReview,
  },
  [PricingAnalyticEvent.AGENT_PUBLISH_ERROR]: {
    description: 'When publishing pricing agent recommendations fails',
    eventName: 'Pricing Agent Publish Error',
    origin: SALES_PORTAL_ROUTES.PricingAgentReview,
  },
  [PricingAnalyticEvent.AGENT_REJECT]: {
    description:
      'When a pricing agent recommendation is rejected on the agent review page',
    eventName: 'Pricing Agent Reject',
    origin: SALES_PORTAL_ROUTES.PricingAgentReview,
  },
  [PricingAnalyticEvent.AGENT_RESET]: {
    description:
      'When a pricing agent recommendation decision is reset back to undecided on the agent review page',
    eventName: 'Pricing Agent Reset',
    origin: SALES_PORTAL_ROUTES.PricingAgentReview,
  },
  [PricingAnalyticEvent.AGENT_UNDO]: {
    description: 'When the undo button is pressed on the agent review page',
    eventName: 'Pricing Agent Undo',
    origin: SALES_PORTAL_ROUTES.PricingAgentReview,
  },
  [PricingAnalyticEvent.BULK_BOOKMARK]: {
    description:
      'When a multi-row selection is bookmarked from the bulk bar, once the note popover closes and the upsert fires (see pageSource, hasNote)',
    eventName: 'Pricing Bulk Bookmark',
    origin: SALES_PORTAL_ROUTES.PricingItemManagement,
  },
  [PricingAnalyticEvent.BULK_EDIT]: {
    description:
      'When a bulk edit is applied to a selection on the item details or customer detail page (see pageSource)',
    eventName: 'Pricing Bulk Edit',
    origin: SALES_PORTAL_ROUTES.PricingItemManagement,
  },
  [PricingAnalyticEvent.BULK_REVERSION]: {
    description:
      'When bulk edits are reverted for a selection on the item details or customer detail page (see pageSource)',
    eventName: 'Pricing Bulk Reversion',
    origin: SALES_PORTAL_ROUTES.PricingItemManagement,
  },
  [PricingAnalyticEvent.BULK_UNBOOKMARK]: {
    description:
      'When a multi-row selection is unbookmarked from the bulk bar (see pageSource)',
    eventName: 'Pricing Bulk Unbookmark',
    origin: SALES_PORTAL_ROUTES.PricingItemManagement,
  },
  [PricingAnalyticEvent.GROUP_BY_TOGGLE]: {
    description:
      'When a grouped pricing table is switched between item and account parents (see pageSource, groupBy)',
    eventName: 'Pricing Group By Toggle',
    origin: SALES_PORTAL_ROUTES.PricingManagementReview,
  },
  [PricingAnalyticEvent.INLINE_BOOKMARK]: {
    description:
      'When a single restaurant row is bookmarked from its pricing cell, once the note popover closes and the upsert fires (see pageSource, hasNote)',
    eventName: 'Pricing Inline Bookmark',
    origin: SALES_PORTAL_ROUTES.PricingItemManagement,
  },
  [PricingAnalyticEvent.INLINE_EDIT]: {
    description:
      'When an inline edit is made to a single restaurant row on the item details page',
    eventName: 'Pricing Inline Edit',
    origin: SALES_PORTAL_ROUTES.PricingItemManagement,
  },
  [PricingAnalyticEvent.INLINE_REVERSION]: {
    description:
      'When an inline edit is reverted for a single restaurant row on the item details page',
    eventName: 'Pricing Inline Reversion',
    origin: SALES_PORTAL_ROUTES.PricingItemManagement,
  },
  [PricingAnalyticEvent.INLINE_UNBOOKMARK]: {
    description:
      'When a single restaurant row is unbookmarked from its pricing cell (see pageSource)',
    eventName: 'Pricing Inline Unbookmark',
    origin: SALES_PORTAL_ROUTES.PricingItemManagement,
  },
  [PricingAnalyticEvent.LOAD_AGENT_REVIEW_PAGE]: {
    description: 'When the pricing agent review page finishes loading',
    eventName: 'Pricing Load Agent Review Page',
    origin: SALES_PORTAL_ROUTES.PricingAgentReview,
  },
  [PricingAnalyticEvent.LOAD_BOOKMARKED_PAGE]: {
    description: 'When the Bookmarked page finishes loading its bookmarks',
    eventName: 'Pricing Load Bookmarked Page',
    origin: SALES_PORTAL_ROUTES.PricingBookmarked,
  },
  [PricingAnalyticEvent.LOAD_COST_CHANGES_CUSTOMER_DETAIL]: {
    description:
      'When the customer detail page finishes loading cost change data',
    eventName: 'Pricing Load Cost Changes Customer Detail',
    origin: SALES_PORTAL_ROUTES.PricingCustomerDetail,
  },
  [PricingAnalyticEvent.LOAD_COST_CHANGES_CUSTOMER_LIST]: {
    description:
      'When the customer list page finishes loading cost change data',
    eventName: 'Pricing Load Cost Changes Customer List',
    origin: SALES_PORTAL_ROUTES.PricingCustomers,
  },
  [PricingAnalyticEvent.LOAD_COST_CHANGES_ITEM_DETAILS]: {
    description: 'When the item details page finishes loading cost change data',
    eventName: 'Pricing Load Cost Changes Item Details',
    origin: SALES_PORTAL_ROUTES.PricingItemManagement,
  },
  [PricingAnalyticEvent.LOAD_COST_CHANGES_ITEM_LIST]: {
    description: 'When the item list page finishes loading cost change data',
    eventName: 'Pricing Load Cost Changes Item List',
    origin: SALES_PORTAL_ROUTES.PricingManagement,
  },
  [PricingAnalyticEvent.LOAD_REVIEW_PAGE]: {
    description: 'When the review page finishes loading',
    eventName: 'Pricing Load Review Page',
  },
  [PricingAnalyticEvent.PUBLISH]: {
    description: 'When price edits are published',
    eventName: 'Pricing Publish',
  },
  [PricingAnalyticEvent.PUBLISH_ERROR]: {
    description: 'When publishing price edits fails',
    eventName: 'Pricing Publish Error',
  },
  [PricingAnalyticEvent.UNDO]: {
    description: 'When the undo button is pressed',
    eventName: 'Pricing Undo',
    origin: SALES_PORTAL_ROUTES.PricingItemManagement,
  },
};

class PricingManagementAnalytics {
  track<E extends PricingAnalyticEvent>(
    event: E,
    payload: PricingAnalyticPayloads[E],
  ) {
    const initialized = Fennel.getInstance().getInitializationState();

    if (!initialized) {
      console.warn('Fennel tracker not initialized. Call initialize() first.');
      captureException(
        new Error('Fennel tracker not initialized. Call initialize() first.'),
      );
      return;
    }

    const meta = pricingEventsMetadata[event];
    initialized.engine.track(meta.eventName, {
      ...initialized.state,
      ...payload,
    });
  }
}

export const pricingManagementAnalytics = new PricingManagementAnalytics();
